/**
 * Facebook Pixel Tracking Utilities
 * Works with backend Conversion API for event deduplication
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Get Facebook tracking cookies (_fbc and _fbp)
 * These are used for better event matching with backend
 */
export function getFacebookCookies() {
  if (typeof window === 'undefined') {
    return { fbc: undefined, fbp: undefined };
  }

  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return undefined;
  };

  return {
    fbc: getCookie('_fbc'), // Facebook click ID
    fbp: getCookie('_fbp'), // Facebook browser ID
  };
}

/**
 * Track event with Facebook Pixel
 * Returns eventId for deduplication with backend
 */
export function trackFacebookEvent(
  eventName: string,
  data?: Record<string, any>,
  eventId?: string
): string {
  const id = eventId || uuidv4();

  if (typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq('track', eventName, data || {}, { eventID: id });
    } catch (error) {
      console.error('Facebook Pixel tracking error:', error);
    }
  }

  return id;
}

/**
 * Track page view
 */
export function trackPageView() {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq('track', 'PageView');
    } catch (error) {
      console.error('Facebook Pixel page view error:', error);
    }
  }
}

/**
 * Get complete tracking data for API calls
 * Includes cookies, eventId, and user agent
 */
export function getFacebookTrackingData(eventId?: string) {
  const { fbc, fbp } = getFacebookCookies();
  
  return {
    fbc,
    fbp,
    eventId: eventId || uuidv4(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };
}

// TypeScript declarations
declare global {
  interface Window {
    fbq?: (
      action: string,
      eventName: string,
      data?: Record<string, any>,
      options?: { eventID?: string }
    ) => void;
    _fbq?: any;
  }
}

